import { DateTime } from "luxon";
import { useEffect, useRef, useState, useCallback } from "react";
import { isFeatureEnabled } from "./feature-flag";
import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";

export const uuidv4 = () => {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16));
};

export const sluggify = (word: string) => {
    return word
        .toString()
        .toLowerCase()
        .trim() // delete trailing & leading spaces
        .replace("ç", "c")
        .normalize("NFD") //normalize
        .replace(/[\u0300-\u036f]/g, "") // replace diacritics
        .replace(/[^a-zA-Z0-9_. ]/g, " ") //replace all special characters except spaces, underscores and dots
        .replace(/ /g, "_"); //replace all spaces by _
};

export const phoneNumberValidator = (phone_number: string | undefined | null) => {
    if (!phone_number) return false;
    return /^(?:(?:\+|00|)33|0|(?:\+|00|)590|(?:\+|00|)262|(?:\+|00|)594|(?:\+|00|)596|(?:\+|00|)508)\s*[1-9](?:[\s.-]*\d{2}){4}$/gim.test(phone_number);
};

// Mobile numbers: first national digit must be 6 or 7 (+336/+337, +2626/+2627, +5906/+5907, …).
// Un-normalized DOM-TOM still stored as +33690… are covered by the +336/+337 branch.
const MOBILE_PHONE_PATTERN = /^\+(?:33[67]|262[67]|590[67]|594[67]|596[67]|508[67])/;

export const isMobilePhone = (phone_number: string | undefined | null): boolean => {
    if (!phone_number) return false;
    const normalized = phone_number.replace(/[\s.-]/g, "");
    return MOBILE_PHONE_PATTERN.test(normalized);
};

export const featuresToOptions = (features: { properties: { name: string; label: string; postcode: string } }[], value: string) => {
    const v1 = features.filter(f => f.properties.postcode == value);
    const v2 = features.filter(f => f.properties.postcode.slice(0, 2) == value.slice(0, 2));
    const _features = v1.length > 0 ? v1 : v2;
    return _features.map(f => {
        return { value: f.properties?.name, label: f.properties?.label };
    });
};

// Define the useDebounce hook with TypeScript generics to handle values of any type.
export function useDebounce<T>(value: T, delay: number, shouldDebounce: (value: T) => boolean = () => true): T {
    // State to hold the debounced value
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        // Set up a timeout to update the debounced value after the specified delay
        if (timerRef.current) clearTimeout(timerRef.current);

        if (shouldDebounce(value)) {
            timerRef.current = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
        } else {
            setDebouncedValue(value);
        }

        // Cleanup function to clear the timeout when value or delay changes, or on component unmount.
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [value, delay]); // Only re-run effect if value or delay changes.

    return debouncedValue;
}

export function addBusinessDays(startDate: string, businessDaysToAdd: number) {
    let currentDate = DateTime.fromISO(startDate);
    let addedDays = 0;

    while (addedDays < businessDaysToAdd) {
        currentDate = currentDate.plus({ days: 1 });
        // Check if the current day is not Saturday (6) or Sunday (7)
        if (currentDate.weekday !== 6 && currentDate.weekday !== 7) {
            addedDays++;
        }
    }

    return currentDate;
}

export function generateFutureFirstOfMonthOptions({ months = 24, locale = "fr", startFromNextMonth = true }: { months?: number; locale?: string; startFromNextMonth?: boolean } = {}) {
    const options: { value: string; label: string }[] = [];
    const now = DateTime.now();
    const startDate = (startFromNextMonth ? now.plus({ months: 1 }) : now).startOf("month");

    for (let i = 0; i < months; i++) {
        const date = startDate.plus({ months: i });
        const formattedDate = date.toFormat("yyyy-MM-dd");
        const label = date.toFormat("d MMMM yyyy", { locale });
        options.push({
            value: formattedDate,
            label: label.charAt(0).toUpperCase() + label.slice(1)
        });
    }

    return options;
}

/**
 * Options for the first occurrence of a given weekday in each month (e.g. first Tuesday of each month).
 * schoolDay: 1 = Monday, 2 = Tuesday, ... 5 = Friday (Luxon weekday).
 */
export function generateFutureFirstWeekdayOfMonthOptions({
    schoolDay,
    months = 24,
    locale = "fr",
    startFromNextMonth = true,
    valueFormat = "iso",
    /**
     * Optional month from which to start generating options.
     * If provided, this takes precedence over "startFromNextMonth".
     */
    startFromMonth
}: {
    schoolDay: number;
    months?: number;
    locale?: string;
    startFromNextMonth?: boolean;
    valueFormat?: "iso" | "dd/MM/yyyy";
    startFromMonth?: DateTime;
}): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [];
    const now = DateTime.now();
    const baseStart = startFromMonth ?? (startFromNextMonth ? now.plus({ months: 1 }) : now);
    const startMonth = baseStart.startOf("month");

    for (let i = 0; i < months; i++) {
        const firstOfMonth = startMonth.plus({ months: i });
        const daysToAdd = (schoolDay - firstOfMonth.weekday + 7) % 7;
        const date = firstOfMonth.plus({ days: daysToAdd });
        const iso = date.toFormat("yyyy-MM-dd");
        const value = valueFormat === "dd/MM/yyyy" ? date.toFormat("dd/MM/yyyy") : iso;
        const label = date.toFormat("d MMMM yyyy", { locale });
        options.push({
            value,
            label: label.charAt(0).toUpperCase() + label.slice(1)
        });
    }

    return options;
}

export const conversion_seconde_heure = (
    time: number,
    displayDays: boolean = false,
    displaySeconds: boolean = false
    // sentence: boolean = false,
    // modal: boolean = false
): string => {
    time = Math.round(time);
    if (isNaN(time)) return " - ";
    if (time === 0) return "0s";
    const d = Math.floor(time / 86400);
    let h = displayDays ? Math.floor((time % 86400) / 3600) : Math.floor(time / 3600);

    let m = displaySeconds
        ? // If seconds are displayed, the minutes are not rounded because
          // the digits after the decimal will be considered in the seconds calculation
          Math.floor((time % 3600) / 60)
        : // else the minutes have to be rounded to keep the info of the decimals
          Math.round((time % 3600) / 60);

    // check if rounding minutes will make hours roll over
    if (m === 60) {
        h = h + 1;
        m = m - 60;
    }

    const s = (time % 3600) % 60;
    const dDisplay = d > 0 ? (displayDays ? d.toString() + "j" : "") : "";
    const hDisplay = h > 0 ? h.toString() + "h" : "";
    const mDisplay =
        h > 0
            ? m.toLocaleString("fr", {
                  minimumIntegerDigits: 2,
                  useGrouping: false
              })
            : m > 0
              ? m.toLocaleString("fr", {
                    minimumIntegerDigits: 1,
                    useGrouping: false
                }) + `${h === 0 ? " min" : "min"}`
              : "";
    const sDisplay = (s > 0 && displaySeconds) || (m === 0 && h === 0) ? s.toString() + `${m === 0 ? "s" : "s"}` : "";
    const resp = dDisplay + hDisplay + mDisplay + sDisplay;
    if (resp === "") return " - ";
    return resp;
};

export const downloadURL = ({ url, filename }: { url: string; filename: string }) => {
    fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/pdf"
        }
    })
        .then(response => response.blob())
        .then(blob => {
            // Create blob link to download
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();
            // Clean up and remove the link
            link?.parentNode?.removeChild(link);
        });
};

// Durée d'expiration du cache metadata en minutes
export const METADATA_CACHE_EXPIRATION_MINUTES = 5;

export async function fetchOptionsMetadata(endpoint: string) {
    const CACHE_KEY = `options-metadata:${endpoint}`;
    const now = Date.now();

    // Fonction pour vérifier si la réponse correspond à une OptionsResponse
    const isValidOptionsResponse = (data: any): boolean => {
        return data && typeof data === "object" && data.is_access_allowed !== undefined;
    };

    // Vérifier le cache
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
        try {
            const { data, expiresAt } = JSON.parse(cached);
            if (data.is_access_allowed && expiresAt && now < expiresAt) {
                // Vérifier si les données en cache sont valides
                if (isValidOptionsResponse(data)) {
                    return data;
                } else {
                    // Si les données en cache ne sont pas valides, les supprimer
                    localStorage.removeItem(CACHE_KEY);
                }
            } else {
                localStorage.removeItem(CACHE_KEY);
            }
        } catch (e) {
            localStorage.removeItem(CACHE_KEY);
        }
    }

    const stage = (import.meta.env.VITE_RELEASE_STAGE ?? "development") as "production" | "staging" | "development";
    const baseUrl = (await import("@/config")).default.BACKEND_URL[stage];
    const fullUrl = `${baseUrl}${endpoint}`;
    const { AuthService } = await import("@/services/auth");
    const validToken = await AuthService.getValidAccessToken();
    if (!validToken) return null;
    
    const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json"
    };
    headers["Authorization"] = `Bearer ${validToken}`;
    if (isFeatureEnabled("disable-server-cache")) {
        console.log("disable-server-cache");
        headers["Walter-Cache-Control"] = "nocache";
    }

    let metadata = null;
    try {
        const resp = await fetch(fullUrl, {
            method: "OPTIONS",
            credentials: "include",
            headers
        });
        if (resp.ok) {
            metadata = await resp.json();

            // Vérifier si la réponse correspond à une OptionsResponse
            if (isValidOptionsResponse(metadata)) {
                // Stocker dans le cache avec expiration
                localStorage.setItem(
                    CACHE_KEY,
                    JSON.stringify({
                        data: metadata,
                        expiresAt: now + METADATA_CACHE_EXPIRATION_MINUTES * 60 * 1000
                    })
                );
            } else {
                // Si la réponse n'est pas valide, ne pas la mettre en cache
                console.warn(`Invalid OptionsResponse for endpoint ${endpoint}:`, metadata);
                metadata = null;
            }
        } else {
            metadata = null;
        }
    } catch (e) {
        console.error("Error fetching metadata", e);
        metadata = null;
    }
    return metadata;
}

export function useOptionsMetadata(endpoint: string) {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const fetchMetadata = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchOptionsMetadata(endpoint);
            setData(result);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [endpoint]);

    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    return { data, loading, error, refetch: fetchMetadata };
}

export function getApprenticeshipTypeTag(type: ApprenticeshipRegistrationType["apprenticeship_type"]) {
    if (type === "Apprentissage") {
        return "AP";
    } else if (type === "ContratPro") {
        return "CP";
    } else if (type === "Pro-A") {
        return "Pro-A";
    }
    return "N/A";
}
